import React, { useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  ShieldCheck,
  FileText,
  Lock,
  Cookie,
  CreditCard,
  AlertTriangle,
  FileCode,
  LifeBuoy,
  Scale,
  CheckCircle2,
  ChevronRight,
  Mail,
  Building2,
} from 'lucide-react';

export interface PolicyDefinition {
  slug: string;
  title: string;
  category: 'legal' | 'privacy' | 'commerce' | 'operations';
  icon: React.ElementType;
  description: string;
  lastUpdated: string;
  content: {
    summary: string;
    sections: Array<{
      title: string;
      paragraphs: string[];
      bullets?: string[];
    }>;
  };
}

export const POLICY_CATALOG: Record<string, PolicyDefinition> = {
  'terms': {
    slug: 'terms',
    title: 'Terms & Conditions',
    category: 'legal',
    icon: Scale,
    description: 'General governing terms of service for InviteCloud Technologies platform and services.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'These Terms & Conditions govern your access to and use of the InviteCloud platform, digital invitation design tools, and services operated by InviteCloud Technologies under Indian jurisdiction.',
      sections: [
        {
          title: '1. Acceptance of Terms & Electronic Record Audit Trail',
          paragraphs: [
            'By accessing, registering, or using InviteCloud, you acknowledge that you have read, understood, and agreed to these Terms, our Privacy Policy, Acceptable Use Policy, Cookie Policy, and any additional policies published on our Platform.',
            'In accordance with Section 10A of the Information Technology Act, 2000 (validity of contracts formed through electronic means), your affirmative acceptance via the Terms & User Agreement popup generates a timestamped electronic audit record (accepted version, acceptance timestamp, acceptance method) tied to your account. This electronic record constitutes a legally binding agreement between you and InviteCloud Technologies.',
          ],
        },
        {
          title: '2. Operating Entity & Jurisdiction',
          paragraphs: [
            'InviteCloud services are owned and operated by InviteCloud Technologies in India.',
            'These Terms shall be governed by and construed in accordance with the laws of India. Any legal suit, action, or proceeding arising out of these Terms or the Platform shall be subject to the exclusive jurisdiction of the competent courts in India.',
          ],
        },
        {
          title: '3. User Accounts & Responsibilities',
          paragraphs: [
            'To access certain features of the Platform, you must register for an account using Google, Facebook, or Microsoft social authentication.',
            'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.',
          ],
          bullets: [
            'You must provide accurate and complete account information.',
            'You must notify InviteCloud immediately of any unauthorized use of your account.',
            'Account sharing or selling account credentials to third parties is strictly prohibited.',
          ],
        },
        {
          title: '4. Intellectual Property & User Content License',
          paragraphs: [
            'InviteCloud retains all rights, title, and interest in and to the Platform, including design templates, remotion video pipelines, software, branding, and proprietary UI components.',
            'You retain ownership of all photos, text, and personal media uploaded to your invitations. By uploading content, you grant InviteCloud a non-exclusive, worldwide, royalty-free license to host, display, and process your content solely for operating your invitation service.',
          ],
        },
        {
          title: '5. Governing Contact',
          paragraphs: [
            'For any legal notices or terms inquiries, contact us at founder@invitecloud.in or support@invitecloud.in.',
          ],
        },
      ],
    },
  },
  'privacy-policy': {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    category: 'privacy',
    icon: Lock,
    description: 'Data protection standards, DPDP Act 2023 compliance, payment processors, and guest privacy.',
    lastUpdated: 'October 2, 2026',
    content: {
      summary: 'InviteCloud protects personal data under the Digital Personal Data Protection Act, 2023 (DPDP Act). Payments are processed by Razorpay; InviteCloud never stores card PAN, CVC, or full card numbers.',
      sections: [
        {
          title: '1. Data We Collect',
          paragraphs: [
            'We collect minimal personal data necessary to provide our digital invitation and RSVP service.',
          ],
          bullets: [
            'Account Information: Name and email address provided during social authentication.',
            'Invitation Details: Event dates, venue locations, couple names, and uploaded event photos.',
            'Guest Responses: Guest names, RSVP attendance status, dietary preferences, and blessings messages.',
            'Aggregated Telemetry: Pageviews and template interactions via GA4 without capturing PII.',
            'Billing metadata: order amounts, currencies, and Razorpay payment/order ids — never raw card data.',
          ],
        },
        {
          title: '2. Payments & card data',
          paragraphs: [
            'Card and UPI payments are processed exclusively by Razorpay on Razorpay-hosted Checkout. InviteCloud does not collect, transmit, or store card numbers, CVV/CVC, or expiry details on our servers or in first-party UI.',
            'Razorpay may tokenize payment methods for returning customers under its own PCI-DSS scope; InviteCloud only receives non-sensitive payment identifiers.',
          ],
        },
        {
          title: '3. Processors that handle user data',
          paragraphs: [
            'Depending on the feature used, personal or operational data may be processed by:',
          ],
          bullets: [
            'Firebase (authentication)',
            'Cloudflare R2 (media storage and delivery)',
            'Resend (transactional email)',
            'Sentry (error monitoring)',
            'Razorpay (payments)',
            'Google Gemini, OpenAI, Anthropic, fal.ai, HuggingFace (AI generation / moderation, where enabled)',
            'Google Analytics 4 (aggregated product analytics)',
          ],
        },
        {
          title: '4. DPDP Act Compliance & Data Rights',
          paragraphs: [
            'Under the DPDP Act 2023, Indian users retain rights to access, correct, update, and request erasure of their personal data. Consent for account creation and Terms acceptance is recorded with a versioned timestamp.',
            'Named Grievance Officer details (F4) will be published when confirmed. Until then, data-protection grievances may be sent to founder@invitecloud.in and support@invitecloud.in; we aim to acknowledge within 5 business days.',
            'Cross-border transfers may occur for media CDN and AI providers as described in Data Security & Storage. Retention: account and order records are kept while the account is active and as required for tax, dispute, and legal obligations.',
          ],
        },
        {
          title: '5. Data Security & Storage Architecture',
          paragraphs: [
            'Our backend architecture runs on edge-deployed static SPA clients communicating securely over SSL/TLS with centralized backend services.',
            'Primary relational databases are hosted on Azure Database for PostgreSQL Flexible Server in Central India (India) with automated backups. Application compute runs on a DigitalOcean server in Bangalore (India). Uploaded media is stored in Cloudflare R2 and delivered via Cloudflare’s global network, which may involve processing outside India.',
          ],
        },
      ],
    },
  },
  'acceptable-use-policy': {
    slug: 'acceptable-use-policy',
    title: 'Acceptable Use Policy',
    category: 'legal',
    icon: ShieldCheck,
    description: 'Prohibited conduct, automated AI safety checks, and third-party AI provider disclosures.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'This policy defines acceptable platform usage and content standards to ensure InviteCloud remains a safe, respectful environment for all event hosts and guests.',
      sections: [
        {
          title: '1. Prohibited Content & Activity',
          paragraphs: [
            'Users may not create invitations containing unlawful, abusive, defamatory, hateful, or explicit material.',
          ],
          bullets: [
            'Harassment, hate speech, or targeted discrimination against any group or individual.',
            'Unauthorized use of copyrighted artwork, audio tracks, or trademarked logos.',
            'Automated spam invitations, phishing links, or malware distribution.',
          ],
        },
        {
          title: '2. Automated AI Content Moderation & AI Providers',
          paragraphs: [
            'All uploaded invitation media and text are subject to automated AI safety checks prior to public publishing.',
            'AI generation services on InviteCloud utilize trusted third-party providers including Google Gemini API, Cloudflare AI, and Remotion rendering engines.',
          ],
        },
      ],
    },
  },
  'cancellation-refund-policy': {
    slug: 'cancellation-refund-policy',
    title: 'Cancellation & Refund Policy',
    category: 'commerce',
    icon: CreditCard,
    description: 'Order cancellation, refund eligibility, and unique transaction reference tracking.',
    lastUpdated: 'October 11, 2026',
    content: {
      summary: 'Clear guidelines regarding one-time purchases (invitation Collections and Credit Packs), automatic refunds of late payments, and payment dispute resolutions.',
      sections: [
        {
          title: '1. Order & Invoice Identifiers',
          paragraphs: [
            'Every purchase generates a unique system reference ID (TXN-...) and payment gateway reference (pay_...) to track and resolve failed or interrupted payment attempts.',
          ],
        },
        {
          title: '2. How to request a refund',
          paragraphs: [
            'InviteCloud has no subscriptions or automatic renewals; every purchase is one-time. Purchases (invitation Collections and Credit Packs) are non-refundable, including unused or partly used Credit Packs and published invitations, except for faults: not delivered or activated, duplicate or erroneous charge, unauthorized transaction, or a material service failure. There are two timelines. A problem with the product (template will not load, an image is not placed correctly, publish or render failure): a refund is available only where our service has detected the fault or our team has confirmed it after you report it to support@invitecloud.in; a failed publish is retried automatically once first; if it still fails our support team is alerted automatically, we tell you by email and in-app, your dashboard shows the time left, and a 24-hour request window then runs from the time we detect or confirm the fault (enforced by our refund tools, with one reminder about 6 hours before it closes) — it is not open to every customer. Automatic detection currently covers invitation Collections; for a Credit Pack publish failure, report it to support@invitecloud.in and the same window opens once our team confirms it. An administrator checks the invitation, we fix or re-deliver first and refund if we cannot. A problem with the payment itself (duplicate, unauthorized, cancelled or failed charge): duplicates caused by clicking Pay twice or paying across two tabs are handled automatically — refunded to your original payment method for an invitation Collection, or added to your rupee wallet as credit at the pack\'s rate for a partner wallet pack (with an option to request a bank refund from Settings → Refunds within 7 days before using the credits) — and the original purchase and any credits already used stay with you. For unauthorized transactions, cancelled or failed payments, or if an automatic duplicate refund did not happen, report to us and your bank within 3 working days (RBI timelines); late reports are still reviewed. Your bank\'s RBI rules (liability protection if reported within 3 working days; credit within 10 working days of your report) apply separately. A duplicate charge is handled for the duplicate amount only (refund or wallet credit, as above), and the purchase you actually paid for is unaffected. We do not offer partial refunds: a refund is for the whole payment — except a partner wallet pack whose credits were partly used, which refunds what was paid minus the credits used (₹1 per credit; e.g. ₹5,000 paid, 1,999 used → ₹3,001), with its unused credits leaving the wallet when you ask. While a bank dispute (chargeback) on a wallet-pack payment is open, the rupee wallet is on hold; if the bank returns your money, that pack\'s unused credits are removed and the hold stays until support reviews it. A refund removes that purchase\'s unspent credits and the partner features it unlocked. An unpaid checkout expires after 30 minutes, and a payment received after expiry is refunded automatically (on a partner wallet pack it is added to your rupee wallet instead; to get it back to your bank, write to support@invitecloud.in within 7 days, before using it).',
            // FI-03 wallet-credit wording above: pending B19 owner/legal review (2026-10-23).
          ],
        },
      ],
    },
  },
  'shipping-delivery-policy': {
    slug: 'shipping-delivery-policy',
    title: 'Shipping & Delivery Policy',
    category: 'commerce',
    icon: LifeBuoy,
    description: 'Digital delivery of credit packs and partner features — no physical shipment.',
    lastUpdated: 'October 2, 2026',
    content: {
      summary: 'InviteCloud sells digital goods only. There is no physical shipment and no shipping charge. Credits and partner features are delivered to your account after payment confirmation.',
      sections: [
        {
          title: '1. Digital goods only',
          paragraphs: [
            'All products (credit packs, collection purchases, and partner feature unlocks) are digital. We do not ship physical goods and never charge a shipping fee.',
          ],
        },
        {
          title: '2. Delivery to your account',
          paragraphs: [
            'On successful payment capture, fulfilment grants publish credits and any included partner features to the paying account. Delivery means those entitlements appear in your dashboard — not a postal or courier delivery.',
            'Paid credit lots never expire.',
          ],
        },
        {
          title: '3. Timing (matches production systems)',
          paragraphs: [
            'Fast path: after Razorpay Checkout succeeds, the app verifies the purchase and runs fulfilment for that order immediately where possible.',
            'Automated retry: a fulfilment sweep processes pending grants about once per minute.',
            'Reconciliation: a separate sweep runs about every 5 minutes to recover stuck paid orders.',
            'If credits do not appear after a successful payment, do not pay twice. Contact support@invitecloud.in with your ORD- order number and wait for the automated recovery paths above.',
          ],
        },
      ],
    },
  },
  'contact-us': {
    slug: 'contact-us',
    title: 'Contact Us',
    category: 'operations',
    icon: Building2,
    description: 'How to reach InviteCloud for support, queries, and grievances.',
    lastUpdated: 'October 3, 2026',
    content: {
      summary: 'Official public contact channels and registered office address for InviteCloud. Reach our customer support team via WhatsApp and email for assistance with invitations, payments, and account queries.',
      sections: [
        {
          title: '1. Entity & Registered Office',
          paragraphs: [
            'InviteCloud operates the InviteCloud digital invitation platform in India.',
            'Registered Office Address: InviteCloud, Vishal Nagar, Pimple Nilakh, Pune – 411027, Maharashtra, India.',
          ],
        },
        {
          title: '2. WhatsApp & Email Channels',
          paragraphs: [
            'WhatsApp Support: @invitecloud (InviteCloud official WhatsApp — recommended for sharing screenshots, draft links, photo assets, and payment queries).',
            'Customer Support Email: support@invitecloud.in',
            'Founder & Legal Inquiries: founder@invitecloud.in',
          ],
        },
        {
          title: '3. Hours & Response SLA',
          paragraphs: [
            'Business hours: Monday–Friday, 10:00–18:00 IST (excluding Indian public holidays).',
            'We aim to respond to WhatsApp queries within 2–4 hours during business hours and acknowledge support emails within 1–2 business days.',
          ],
        },
        {
          title: '4. Grievances & Disputes',
          paragraphs: [
            'For escalated billing, refund, or consumer grievances, contact our designated Grievance Officer at founder@invitecloud.in or write to our registered office address in Pune.',
          ],
        },
      ],
    },
  },
  'cookies-policy': {
    slug: 'cookies-policy',
    title: 'Cookies Policy',
    category: 'privacy',
    icon: Cookie,
    description: 'Cookie usage, GA4 telemetry disclosure, and zero PII tracking declaration.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'InviteCloud uses essential cookies for user authentication and session security, alongside aggregated GA4 analytics with zero PII captured.',
      sections: [
        {
          title: '1. Essential vs Analytics Cookies',
          paragraphs: [
            'Essential cookies are required for user login and session persistence. Analytics cookies measure overall platform engagement and template popularity.',
          ],
        },
      ],
    },
  },
  'copyrights-policy': {
    slug: 'copyrights-policy',
    title: 'Copyrights Policy',
    category: 'legal',
    icon: FileCode,
    description: 'IP ownership, user license terms, trademark application status, and DMCA notice process.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Protects intellectual property rights of creators and InviteCloud Technologies while detailing copyright notice submission procedures.',
      sections: [
        {
          title: '1. Trademark & Content Protection',
          paragraphs: [
            'InviteCloud trademark registration is in progress under Indian intellectual property laws. Users retain full rights to their original uploaded media.',
          ],
        },
      ],
    },
  },
  'content-moderation-policy': {
    slug: 'content-moderation-policy',
    title: 'Content Moderation Policy',
    category: 'operations',
    icon: AlertTriangle,
    description: 'Moderation workflow, grievance officer details, and content appeal procedures.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Explains how content complaints are investigated, actioned, and appealed under Indian Intermediary Guidelines.',
      sections: [
        {
          title: '1. Grievance Redressal Mechanism',
          paragraphs: [
            'Grievance complaints may be submitted to our appointed Grievance Officer at founder@invitecloud.in and support@invitecloud.in.',
          ],
        },
      ],
    },
  },
  'disclaimer': {
    slug: 'disclaimer',
    title: 'Disclaimer',
    category: 'legal',
    icon: FileText,
    description: 'Limitation of liability, service availability disclaimers, and consumer protection rights.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Outlines service availability terms, AI generation disclaimers, and statutory consumer rights under Indian law.',
      sections: [
        {
          title: '1. Service & AI Generation Disclaimer',
          paragraphs: [
            'Platform services and AI generation features are provided on an "as is" and "as available" basis.',
          ],
        },
      ],
    },
  },
  'security-policy': {
    slug: 'security-policy',
    title: 'Security Policy',
    category: 'operations',
    icon: ShieldCheck,
    description: 'Decoupled architecture, edge CDN security, DB encryption, and vulnerability disclosure.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Describes our technical infrastructure, decoupled microservices model, and security disclosure policies.',
      sections: [
        {
          title: '1. Decoupled Edge Architecture',
          paragraphs: [
            'InviteCloud runs a decoupled architecture featuring static React SPA edge deployment communicating securely with central NestJS API backends.',
          ],
        },
      ],
    },
  },
  'subscription-policy': {
    slug: 'subscription-policy',
    title: 'Credit Pack & Usage Policy',
    category: 'commerce',
    icon: CreditCard,
    description: 'One-time credit pack purchases, non-expiring credits, permanent tier unlocks, and taxes & invoicing.',
    lastUpdated: 'October 2, 2026',
    content: {
      summary: 'Governs one-time credit packs for event partners, non-expiring credit rollovers, permanent tier feature entitlements, and invoicing.',
      sections: [
        {
          title: '1. One-Time Credit Packs (No Recurring Billing)',
          paragraphs: [
            'InviteCloud operates on an on-demand, one-time payment credit pack model. Partners purchase credit packs (e.g. Basic 10 credits, Intermediate 30 credits, Advanced 73 credits) as one-time transactions.',
            'InviteCloud does not operate recurring calendar subscriptions, automatic renewals, or standing bank e-mandates. You are only charged when you affirmatively choose to purchase a new pack.',
          ],
        },
        {
          title: '2. Non-Expiring Credits & Lifetime Rollover',
          paragraphs: [
            'All purchased credits roll over indefinitely and never expire. Unspent credits remain permanently attached to your account regardless of account activity or elapsed time.',
          ],
        },
        {
          title: '3. Permanent Partner Feature Entitlements',
          paragraphs: [
            'Purchasing a credit pack unlocks its corresponding feature tier (e.g. custom branding, analytics, multi-client management) permanently. Even if your credit balance reaches zero between client events, your unlocked feature tier remains active.',
          ],
        },
        {
          title: '4. Taxes & Invoicing',
          paragraphs: [
            'All credit pack prices are clearly displayed in Indian Rupees (INR) and are inclusive of all applicable taxes. GST is not currently charged. An invoice is generated for every completed purchase.',
          ],
        },
      ],
    },
  },
  'support-policy': {
    slug: 'support-policy',
    title: 'Support Policy',
    category: 'operations',
    icon: LifeBuoy,
    description: 'In-app help desk (/dashboard/help), support SLA targets, and AI generation assistance.',
    lastUpdated: 'July 26, 2026',
    content: {
      summary: 'Details support channels including the in-app ticketing dashboard (/dashboard/help) and AI video support scope.',
      sections: [
        {
          title: '1. Support Channels & Ticketing',
          paragraphs: [
            'Users can submit help tickets directly via /dashboard/help or email support@invitecloud.in and founder@invitecloud.in.',
          ],
        },
      ],
    },
  },
};

export function PolicyPage({ defaultSlug }: { defaultSlug?: string }) {
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const location = useLocation();

  // Determine target policy slug
  const activeSlug = useMemo(() => {
    if (defaultSlug) return defaultSlug;
    if (routeSlug) return routeSlug;
    // Fallback based on pathname if direct route hit like /privacy-policy
    const path = location.pathname.replace(/^\//, '');
    if (path === 'privacy' || path === 'privacy-policy') return 'privacy-policy';
    if (path === 'terms-and-conditions' || path === 'terms') return 'terms';
    if (path === 'refund-policy') return 'cancellation-refund-policy';
    return POLICY_CATALOG[path] ? path : 'terms';
  }, [defaultSlug, routeSlug, location.pathname]);

  const policy = POLICY_CATALOG[activeSlug] || POLICY_CATALOG['terms'];

  // Update browser tab title for SEO and direct link sharing.
  useEffect(() => {
    const prev = document.title;
    document.title = `${policy.title} | InviteCloud`;
    return () => { document.title = prev; };
  }, [policy.title]);

  return (
    <div className="min-h-screen bg-surface-lowest text-on-surface">
      {/* Policy Header Banner */}
      <section className="relative overflow-hidden border-b border-outline-variant/20 bg-surface/50 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1 text-xs font-bold text-primary">
              <policy.icon className="h-3.5 w-3.5" />
              InviteCloud Official Policy
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-on-surface">
              {policy.title}
            </h1>
            <p className="text-sm text-on-surface-variant font-medium">
              {policy.description}
            </p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-1.5 text-xs text-on-surface-variant bg-white/80 p-4 rounded-2xl border border-outline-variant/30 shadow-sm">
            <div className="flex items-center gap-1.5 font-bold text-on-surface">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Effective Date: {policy.lastUpdated}
            </div>
            <span>Entity: InviteCloud Technologies</span>
            <span>Jurisdiction: India</span>
          </div>
        </div>
      </section>

      {/* Main Layout Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* Sticky Left Navigation Sidebar */}
        <aside className="space-y-6">
          <div className="sticky top-24 rounded-2xl border border-outline-variant/30 bg-white p-5 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant px-2">
              Platform Policy Index
            </h3>
            <nav className="space-y-1">
              {Object.values(POLICY_CATALOG).map((item) => {
                const isActive = item.slug === activeSlug;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.slug}
                    to={`/${item.slug}`}
                    className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-xs font-bold transition-all ${
                      isActive
                        ? 'bg-primary text-on-primary shadow-sm'
                        : 'text-on-surface-variant hover:bg-surface-lowest hover:text-on-surface'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 opacity-60 ${isActive ? 'text-on-primary' : ''}`} />
                  </Link>
                );
              })}
            </nav>

            <div className="pt-4 border-t border-outline-variant/20 space-y-3">
              <h4 className="text-xs font-bold text-on-surface px-2">Grievance & Support</h4>
              <a
                href="mailto:founder@invitecloud.in"
                className="flex items-center gap-2 text-xs font-medium text-on-surface-variant hover:text-primary transition-colors px-2"
              >
                <Mail className="h-3.5 w-3.5 text-primary" />
                founder@invitecloud.in
              </a>
              <a
                href="mailto:support@invitecloud.in"
                className="flex items-center gap-2 text-xs font-medium text-on-surface-variant hover:text-primary transition-colors px-2"
              >
                <Mail className="h-3.5 w-3.5 text-primary" />
                support@invitecloud.in
              </a>
            </div>
          </div>
        </aside>

        {/* Policy Document Content Viewer */}
        <main className="space-y-8">
          {/* Executive Summary Box */}
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 space-y-2">
            <h3 className="text-sm font-bold text-primary flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Executive Summary
            </h3>
            <p className="text-xs font-medium text-on-surface leading-relaxed">
              {policy.content.summary}
            </p>
          </div>

          {/* Policy Sections */}
          <div className="rounded-3xl border border-outline-variant/30 bg-white p-6 sm:p-10 shadow-sm space-y-8">
            {policy.content.sections.map((section, idx) => (
              <div key={idx} className="space-y-4 border-b border-outline-variant/20 last:border-0 pb-8 last:pb-0">
                <h3 className="text-xl font-bold text-on-surface">
                  {section.title}
                </h3>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-sm text-on-surface-variant leading-relaxed">
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="space-y-2 pt-1">
                    {section.bullets.map((b, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2.5 text-xs text-on-surface-variant font-medium">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Footer Contact Card */}
          <div className="rounded-2xl border border-outline-variant/30 bg-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary" />
              <div>
                <h4 className="text-sm font-bold text-on-surface">InviteCloud Technologies Legal Desk</h4>
                <p className="text-xs text-on-surface-variant">Operating entity under Indian jurisdiction (invitecloud.in)</p>
              </div>
            </div>
            <a
              href="mailto:founder@invitecloud.in"
              className="px-4 py-2 rounded-xl bg-surface border border-outline-variant text-xs font-bold text-on-surface hover:border-primary transition-all"
            >
              Contact Grievance Officer
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}

export default PolicyPage;
